// Stable, dependency-free hash of the analyzer-relevant pedigree structure,
// used purely to memoise recompute in the hook. Must be stable across runs for
// an unchanged pedigree (independent of object key order / array order).

function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

// The variant interpretations that feed the analyzer's pathogenicity verdict
// (tokenise.js → normaliseGenotype → interpretation / clinvarConflict). They are
// part of the analysis input, so they must be part of the memo key: a ClinVar
// reclassification pathogenic→benign with no carrierStatus change used to leave
// the hash identical, and the hook then served conclusions derived from the OLD
// classification until some unrelated structural edit moved the hash
// (TGCLOUD-1712 PA-4). Sorted, so variant array order still doesn't matter.
function canonicalVariants(g) {
  return (Array.isArray(g && g.variants) ? g.variants : [])
    .map((v) =>
      [
        (v && v.labInterpretation) || '',
        (v && v.clinicianInterpretation) || '',
        (v && v.clinvarInterpretation) || ''
      ].join('/')
    )
    .filter((s) => s !== '//')
    .sort();
}

// Canonicalise a member to only the fields that affect analysis, sorted so the
// hash is order-independent.
function canonicalMember(m) {
  const diagnoses = (m.diagnoses || [])
    .filter((d) => d && d.id)
    // affectedStatus is a clinician downgrade (carrier/unaffected/unknown) that
    // moves the condition out of the analysis affected-set under
    // conditionCarrierEnabled — omitting it made that edit invisible here.
    .map(
      (d) => `${d.id}:${d.displayOnPedigree ? 1 : 0}:${d.affectedStatus || ''}`
    )
    .sort();
  const genotypes = (m.genotypes || [])
    .map((g) => {
      const gene = String((g && (g.symbol || g.gene)) || '').trim();
      const status = (g && g.carrierStatus) || '';
      const variants = canonicalVariants(g);
      // Drop a wholly empty genotype row (as before), but keep one that carries
      // only interpretations — those still reach the analyzer.
      if (!gene && !status && variants.length === 0) return '';
      return `${gene}:${status}:${variants.join(';')}`;
    })
    .filter(Boolean)
    .sort();
  const phenotypes = (m.phenotypes || [])
    .filter((p) => p && p.id)
    .map((p) => `${p.id}:${p.observed === false ? 0 : 1}`)
    .sort();
  return [
    String(m.pid),
    m.gender || 'u',
    m.fatherId || '-',
    m.motherId || '-',
    (Array.isArray(m.spouseIds)
      ? [...m.spouseIds].map(String).sort()
      : []
    ).join(','),
    m.dod || m.dob || '',
    diagnoses.join('|'),
    genotypes.join('|'),
    phenotypes.join('|')
  ].join('~');
}

export function structureHash(members = []) {
  const canon = members
    .filter((m) => m && m.pid != null)
    .map(canonicalMember)
    .sort()
    .join('\n');
  return fnv1a(canon);
}
